/* @flow */
/**
 * Redux Toolkit slice for operational metrics dashboard filters, chart state, and
 * chart data requests.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import { toggleSpinner } from '../dialogStates/dialogStatesSlice';
import { mapFiltersWithDatesToRequest } from '../../helpers/dataHelpers';
import { cryptoRand, getArrayofYears } from '../../helpers/common';
import operationalMetricsFieldMapping from '../../constants/fieldMappings/operationalMetricsFieldMapping';
import {
  docTypeOptions
} from '../../constants/staticData';
import { calculateDateFromInputRange } from '../../nikeCustomComponents/nikeFormInput/DateRange/staticDateRangeData';

export const name = 'operationMetricsDashboard';
const defaultFilters = {
  [operationalMetricsFieldMapping.poCreatedOn]: {},
  [operationalMetricsFieldMapping.documentDate]: {},
  [operationalMetricsFieldMapping.originalGoodsAtConsolidatorDate]: {},
  [operationalMetricsFieldMapping.goodsAtConsolidatorDate]: {},
  [operationalMetricsFieldMapping.geographyCode]: '',
  [operationalMetricsFieldMapping.division]: [],
  [operationalMetricsFieldMapping.vendorCode]: [],
  [operationalMetricsFieldMapping.poDocTypeCode]: [],
  [operationalMetricsFieldMapping.purchaseOrgCode]: [],
  [operationalMetricsFieldMapping.productionNikeLiaisonOffice]: [],
  [operationalMetricsFieldMapping.seasonCode]: [],
  [operationalMetricsFieldMapping.seasonYear]: [],
  [operationalMetricsFieldMapping.productCode]: [],
  [operationalMetricsFieldMapping.categoryCode]: [],
  [operationalMetricsFieldMapping.subCategoryCode]: []
};

// Default PO charts show a rolling two-week document-date window.
const docDatePo = calculateDateFromInputRange({
  startFromDays: -13,
  endFromDays: 0
});

// Default GAC charts show a rolling sixty-day goods-at-consolidator window.
const gacDateGIP = calculateDateFromInputRange({
  startFromDays: -60,
  endFromDays: 0
});

const defaultPOFilters = {
  [operationalMetricsFieldMapping.documentDate]: {
    from: docDatePo.startDate,
    to: docDatePo.endDate
  }
};

const defaultGACFilters = {
  [operationalMetricsFieldMapping.goodsAtConsolidatorDate]: {
    from: gacDateGIP.startDate,
    to: gacDateGIP.endDate
  }
};

/**
 * Extracts option values from static dropdown option objects.
 */
const modifier = (arrayOfObjects) => arrayOfObjects.map((obj) => obj.value);

function createInitialState() {
  return {
    itemViewStatus: {
      poCount: false,
      rejectedPOCount: false,
      modeOfTransportationCount: false,
      manualVsSystemPoCount: false,
      gacDateCount: false
    },
    tableViewStatus: {
      PO: {},
      GAC: {}
    },
    currentTab: 0,
    userProfile: [],
    currentTabName: 'PO',
    filterDropdownValues: {
      [operationalMetricsFieldMapping.seasonYear]: modifier(
        getArrayofYears(2019, 2028)
      ),
      [operationalMetricsFieldMapping.geographyCode]: [
        { text: 'NA', value: 'NA' },
        { text: 'EMEA', value: 'EMEA' },
        { text: 'GC', value: 'GC' },
        { text: 'APLA', value: 'APLA' }
      ],
      [operationalMetricsFieldMapping.seasonCode]: modifier([
        { id: 'FA', value: 'FA' },
        { id: 'SU', value: 'SU' },
        { id: 'HO', value: 'HO' },
        { id: 'SP', value: 'SP' }
      ]),
      [operationalMetricsFieldMapping.division]: modifier([
        { id: '10', value: '10 | Apparel' },
        { id: '20', value: '20 | Footwear' },
        { id: '30', value: '30 | Accessories' }
      ]),
      [operationalMetricsFieldMapping.purchaseOrgCode]: modifier([
        { id: '3100', value: '3100 | Regional Single AP' },
        { id: '3101', value: '3101 | Reg.Single Sample AP' },
        { id: '3150', value: '3150 | Regional-Multi AP' },
        { id: '3151', value: '3151 | Reg-Multi Sample AP' },
        { id: '3155', value: '3155 | Country Managed AP' },
        { id: '3200', value: '3200 | Regional Single EQ' },
        { id: '3201', value: '3201 | Equipment CMP' },
        { id: '3300', value: '3300 | Regional Single FW' },
        { id: '3301', value: '3301 | Country Managed FW' },
        { id: 'FW00', value: 'FW00 | Footwear' },
        { id: 'AP00', value: 'AP00 | Apparel' },
        { id: 'EQ00', value: 'EQ00 | Equipment' }
      ]),
      [operationalMetricsFieldMapping.poDocTypeCode]: modifier(docTypeOptions)
    },
    selectedFiltersPO: {
      ...defaultFilters,
      ...defaultPOFilters
    },
    prevSelectedFiltersPO: {
      ...defaultFilters,
      ...defaultPOFilters
    },
    prevSelectedFiltersGAC: {
      ...defaultFilters,
      ...defaultGACFilters
    },
    selectedFiltersGAC: {
      ...defaultFilters,
      ...defaultGACFilters
    },
    filterErrorPO: {},
    filterErrorGAC: {},
    POChartsId: '',
    GACChartsId: ''
  };
}

export const initialState = createInitialState();

// UPDATE_OPERATIONAL_METRIC_PO__SELECTED_FILTER_VALUES
/**
 * Updates selected PO metric filters and refreshes the PO chart state key.
 */
function updateOperationMetricsPOSelectedFilterValues(state: Object, action: Object) {
  const payload = {
    filter: action.payload.filter,
    values: action.payload.values
  };

  if (!payload.filter) return { ...state };
  const modifiedSelectedFilters = {
    ...state.selectedFiltersPO,
    [payload.filter]: payload.values
  };
  return {
    ...state,
    selectedFiltersPO: modifiedSelectedFilters,
    poChartsId: cryptoRand().toString().slice(2, 10)
  };
}

// UPDATE_OPERATIONAL_METRIC_GAC__SELECTED_FILTER_VALUES
/**
 * Updates selected GAC metric filters and refreshes the GAC chart state key.
 */
function updateOperationMetricsGACSelectedFilterValues(state: Object, action: Object) {
  const payload = {
    filter: action.payload.filter,
    values: action.payload.values
  };

  if (!payload.filter) return { ...state };
  const modifiedSelectedFilters = {
    ...state.selectedFiltersGAC,
    [payload.filter]: payload.values
  };
  return {
    ...state,
    selectedFiltersGAC: modifiedSelectedFilters,
    gacChartsId: cryptoRand().toString().slice(2, 10)
  };
}

// UPDATE_CHART_DATA
/**
 * Stores returned chart data under the chart-specific state key.
 */
function updateChartData(state: Object, action: Object) {
  return {
    ...state,
    [action.payload.chart]: action.payload.data
  };
}

// UPDATE_CHART_FAILURE
/**
 * Stores the failure flag for a chart-specific request.
 */
function updateChartFailure(state: Object, action: Object) {
  return {
    ...state,
    // Failure flags are stored beside chart data using chart-specific keys.
    [`${action.payload.chart}_failed`]: action.payload.data
  };
}

// UPDATE_CHART_LOADING
/**
 * Stores the loading flag for a chart-specific request.
 */
function updateChartLoading(state: Object, action: Object) {
  const payload = {
    chart: action.payload.chart,
    data: action.payload.data
  };
  return {
    ...state,
    // Loading flags mirror chart-specific data keys consumed by the dashboard.
    [`${payload.chart}_loading`]: payload.data
  };
}

// UPDATE_CHART_ITEM_VIEW
/**
 * Updates card-level item view visibility for operational metric charts.
 */
function updateChartItemView(state: Object, action: Object) {
  return {
    ...state,
    itemViewStatus: action.payload
  };
}

// UPDATE_CHART_TABLE_VIEW
/**
 * Updates table view visibility for a chart within the selected metrics tab.
 */
function updateChartTableView(state: Object, action: Object) {
  return {
    ...state,
    tableViewStatus: {
      ...state.tableViewStatus,
      [action.payload.tab]: {
        ...state.tableViewStatus[action.payload.tab],
        // Table-view status is tracked per tab and chart.
        [action.payload.chart]: action.payload.value
      }
    }
  };
}

// UPDATE_CHART_TAB
/**
 * Updates the active operational metrics dashboard tab.
 */
function updateChartTab(state: Object, action: Object) {
  return {
    ...state,
    currentTab: action.payload
  };
}

// RESET_METRICS_FILTERS
/**
 * Resets filters, previous filters, errors, and chart refresh IDs for a tab.
 */
function resetMetricsSearch(state: Object, action: Object) {
  return {
    ...state,
    // PO and GAC tabs share reducer logic via tab-suffixed state keys.
    [`selectedFilters${action.payload}`]: { ...initialState[`selectedFilters${action.payload}`] },
    [`prevSelectedFilters${action.payload}`]: { ...initialState[`prevSelectedFilters${action.payload}`] },
    [`filterError${action.payload}`]: { ...initialState[`filterError${action.payload}`] },
    [`${action.payload}ChartsId`]: ''
  };
}

// UPDATE_OPERATIONAL_METRICS_PREV_SEARCH
/**
 * Stores the last applied PO filters for change detection.
 */
function updateOMPreviousSelectedFiltersForPO(state: Object, action: Object) {
  return {
    ...state,
    prevSelectedFiltersPO: action.payload
  };
}

/**
 * Stores the last applied GAC filters for change detection.
 */
function updateOMPreviousSelectedFiltersForGAC(state: Object, action: Object) {
  return {
    ...state,
    prevSelectedFiltersGAC: action.payload
  };
}

// UPDATE_OPERATIONAL_METRIC_PO_ERROR
// UPDATE_OPERATIONAL_METRIC_GAC_ERROR
/**
 * Updates filter validation errors for the PO or GAC metrics tab.
 */
function updateOperationMetricsPOError(state: Object, action: Object) {
  if (action.payload.tab === 'PO') {
    return {
      ...state,
      filterErrorPO: { ...state.filterErrorPO, [action.payload.field]: action.payload.error }
    };
  }
  return {
    ...state,
    filterErrorGAC: { ...state.filterErrorGAC, [action.payload.field]: action.payload.error }
  };
}

/**
 * Collects reducers that manage operational metrics dashboard state.
 */
function createReducers() {
  return {
    updateOperationMetricsPOSelectedFilterValues,
    updateOperationMetricsGACSelectedFilterValues,
    updateChartData,
    updateChartFailure,
    updateChartLoading,
    updateChartItemView,
    updateChartTableView,
    updateChartTab,
    resetMetricsSearch,
    updateOMPreviousSelectedFiltersForPO,
    updateOMPreviousSelectedFiltersForGAC,
    updateOperationMetricsPOError
  };
}

export const reducers = createReducers();
export const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };

/**
 * Creates a thunk that applies changed filter values for the active metrics tab.
 */
function applyFilters() {
  return createAsyncThunk(
    `${name}/applyFilters`,
    async (
      {
        payload,
        tab,
        callback
      },
      { getState, dispatch }
    ) => {
      const state = getState();
      const selectedFilters = state.OperationalMetrics[`selectedFilters${tab}`];
      const selectedFilter = selectedFilters[payload.filter];
      const oldValue = JSON.stringify(selectedFilter);
      const newValue = JSON.stringify(payload.values);
      // String comparison preserves existing deep-equality behavior for filter values.
      const hasChanged = oldValue !== newValue;
      if (!hasChanged) {
        return false;
      }
      if (tab === 'PO') {
        dispatch(actions.updateOperationMetricsPOSelectedFilterValues(payload));
      } else if (tab === 'GAC') {
        dispatch(actions.updateOperationMetricsGACSelectedFilterValues(payload));
      }
      if (callback) {
        callback();
      }
      return true;
    }
  );
}

/**
 * Creates a thunk that appends one selected value to a metrics filter.
 */
function addValueToFilters() {
  return createAsyncThunk(
    `${name}/addValueToFilters`,
    async (
      {
        payload,
        tab,
        callback
      },
      { getState, dispatch }
    ) => {
      const state = getState();
      const selectedFilters = state.OperationalMetrics[`selectedFilters${tab}`];
      const filterValues = selectedFilters[payload.filter] || [];
      const newPayload = { ...payload, values: [...filterValues, payload.value] };
      if (tab === 'PO') {
        dispatch(actions.updateOperationMetricsPOSelectedFilterValues(newPayload));
      } else {
        dispatch(actions.updateOperationMetricsGACSelectedFilterValues(newPayload));
      }
      if (callback) {
        callback();
      }
    }
  );
}

/**
 * Creates a thunk that snapshots current filters when they differ from previous filters.
 */
function updateOperationalMetricsPreviousSearch() {
  return createAsyncThunk(
    `${name}/updateOperationalMetricsPreviousSearch`,
    async (
      {
        tab,
        callback
      },
      { getState, dispatch }
    ) => {
      const state = getState();
      let currentFilters;
      let prevFilters;
      if (tab === 'PO') {
        currentFilters = state.OperationalMetrics.selectedFiltersPO;
        prevFilters = state.OperationalMetrics.prevSelectedFiltersPO;
      } else {
        currentFilters = state.OperationalMetrics.selectedFiltersGAC;
        prevFilters = state.OperationalMetrics.prevSelectedFiltersGAC;
      }
      const hasChanged = JSON.stringify(currentFilters) !== JSON.stringify(prevFilters);
      if (hasChanged) {
        if (tab === 'PO') {
          dispatch(actions.updateOMPreviousSelectedFiltersForPO(currentFilters));
        } else {
          dispatch(actions.updateOMPreviousSelectedFiltersForGAC(currentFilters));
        }
      }
      callback(hasChanged);
    }
  );
}

/**
 * Creates a thunk that requests chart data from the metricsChartData API.
 */
function callMetricsChartDataAPI() {
  return createAsyncThunk(
    `${name}/callMetricsChartDataAPI`,
    async ({
      chart,
      search,
      callback
    }, { dispatch }) => {
      dispatch(toggleSpinner(true));
      request({
        api: 'metricsChartData',
        method: 'post',
        cancellable: true,
        suppressErrorDialog: true,
        data: {
          graph: chart,
          search
        }
      }, dispatch).then((response) => {
        dispatch(toggleSpinner(false));
        dispatch(actions.updateChartLoading({ chart, data: false }));
        // Each metrics API response is keyed by the requested chart name.
        let data = response.data[chart];
        if (data && (chart === 'rejectedPOCount' || chart === 'rejectedPOItemQtySum')) {
          switch (chart) {
            case 'rejectedPOCount':
              data = data.map((rejectionData) => (
                {
                  poRejectionCode: `${rejectionData.poRejectionCode} | ${rejectionData.poRejectionCodeDescription}`,
                  count: rejectionData.count
                }
              ));
              break;
            case 'rejectedPOItemQtySum':
              data = data.map((rejectionData) => (
                {
                  poRejectionCode: `${rejectionData.poRejectionCode} | ${rejectionData.poRejectionCodeDescription}`,
                  totalItmQty: rejectionData.totalItmQty
                }
              ));
              break;
            default:
              break;
          }
        }
        dispatch(actions.updateChartData({ chart, data }));
        if (callback) {
          callback(response.data);
        }
      }).catch((error) => {
        if (callback) {
          callback(null, error);
        }
        dispatch(toggleSpinner(false));
        dispatch(actions.updateChartLoading({ chart, data: false }));
        dispatch(actions.updateChartFailure({ chart, data: true }));
      });
    }
  );
}

/**
 * Creates a thunk that builds chart search criteria and conditionally requests chart data.
 */
function requestChartData() {
  return createAsyncThunk(
    `${name}/requestChartData`,
    async (
      {
        chart, tab, forceLoad, newRequest, callback, OMCallMetricsChartDataAPI
      },
      { getState, dispatch }
    ) => {
      const state = getState();
      // New requests use current filters; cached chart refreshes use the last applied filters.
      const searchData = state.OperationalMetrics[newRequest ? `selectedFilters${tab}` : `prevSelectedFilters${tab}`];
      const search = mapFiltersWithDatesToRequest(searchData, 'operationalMetrics');
      // This has been purposefully done for Open PO's
      if (chart === 'modeOfTransportationQtySum' || chart === 'modeOfTransportationCount'
        || chart === 'manualVsSystemPoCount' || chart === 'manualVsSystemQtySum'
        || chart === 'gacDateCount' || chart === 'gacDateQtySum'
      ) {
        search.push(
          {
            fieldName: 'dpomItemStatus',
            operator: '=',
            fieldValue: [
              'Rejected', 'Accepted', 'Unissued', 'Unaccepted'
            ]
          }
        );
      }
      const chartData = state.OperationalMetrics[chart];
      const errorState = state.OperationalMetrics.filterErrorPO;
      const hasError = Object.keys(errorState).find((filter) => errorState[filter]);

      // Reuse cached chart data unless a reload is forced or filter validation failed.
      if ((chartData && !forceLoad) || hasError) {
        if (callback) {
          callback(chartData);
        }
        return false;
      }
      dispatch(actions.updateChartFailure({ chart, data: false }));
      dispatch(actions.updateChartLoading({ chart, data: true }));
      dispatch(OMCallMetricsChartDataAPI({
        chart, search, callback
      }));
      return true;
    }
  );
}

/**
 * Instantiates async operational metrics actions exposed by the slice.
 */
function createExtraActions() {
  return {
    applyFilters: applyFilters(),
    addValueToFilters: addValueToFilters(),
    updateOperationalMetricsPreviousSearch: updateOperationalMetricsPreviousSearch(),
    callMetricsChartDataAPI: callMetricsChartDataAPI(),
    requestChartData: requestChartData()
  };
}
export const extraActions = createExtraActions();

export const operationalMetricsActions = { ...actions, ...extraActions };
export const operationalMetricsReducer = slice.reducer;
